class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const updateEvent = FacetFiltersForm.startUpdateEvent(searchParams);
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event, updateEvent)
        : FacetFiltersForm.renderSectionFromFetch(url, event, updateEvent);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static startUpdateEvent(searchParams) {
    const { SearchUpdateEvent, CollectionUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent && !CollectionUpdateEvent) return;

    const facetsForm = document.querySelector('facet-filters-form');
    const facetsContainer = document.querySelector('.facets-container');
    const collectionId = facetsContainer?.dataset.collectionId || null;
    const collectionHandle = facetsContainer?.dataset.collectionHandle || '';
    const currentCount = parseInt(document.getElementById('ProductCount')?.dataset.productCount) || 0;
    const urlSearchParams = new URLSearchParams(searchParams);
    const isSearchPage = facetsContainer?.dataset.template === 'search';
    const isCollectionPage = facetsContainer?.dataset.template === 'collection';
    const dispatchTarget = facetsForm || document;

    let deferred;

    if (isSearchPage) {
      deferred = SearchUpdateEvent.createPromise();
      dispatchTarget.dispatchEvent(
        new SearchUpdateEvent({
          search: {
            query: urlSearchParams.get('q') || '',
            productFilters: SearchUpdateEvent.parseProductFilters(urlSearchParams),
            sortKey: SearchUpdateEvent.getSortKey(urlSearchParams),
          },
          promise: deferred.promise,
        }),
      );
    } else if (isCollectionPage) {
      // collectionId is null for auto-generated collections (/collections/all,
      // /collections/vendors/<x>, etc.) — the SSE schema accepts that.
      deferred = CollectionUpdateEvent.createPromise();
      dispatchTarget.dispatchEvent(
        new CollectionUpdateEvent({
          collection: { id: collectionId, handle: collectionHandle, productsCount: currentCount },
          productFilters: CollectionUpdateEvent.parseProductFilters(urlSearchParams),
          sortKey: CollectionUpdateEvent.getSortKey(urlSearchParams),
          promise: deferred.promise,
        }),
      );
    }

    if (!deferred) return;

    return {
      resolve: (filteredCount) => {
        deferred.resolve({ [isSearchPage ? 'totalCount' : 'productsCount']: filteredCount });
      },
      reject: deferred.reject,
    };
  }

  static renderSectionFromFetch(url, event, updateEvent) {
    fetch(url)
      .then((response) => response.text())
      .then((html) => {
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderSection(html, event, updateEvent);
      })
      .catch((error) => {
        console.error(error);
        updateEvent?.reject(error);
      });
  }

  static renderSectionFromCache(filterDataUrl, event, updateEvent) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderSection(html, event, updateEvent);
  }

  static renderSection(html, event, updateEvent) {
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html, updateEvent);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html, updateEvent) {
    const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
    const sourceCount = parsedHtml.getElementById('ProductCount');
    const count = sourceCount.innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.dataset.productCount = sourceCount.dataset.productCount || '';
    container.dataset.totalCount = sourceCount.dataset.totalCount || '';
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));

    updateEvent?.resolve(parseInt(sourceCount.dataset.productCount) || 0);
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (!isTextInput) {
          // Try to return focus to the same checkbox the user just toggled,
          // re-selecting it from the freshly rendered HTML by its id.
          const originatingInputId = event.target.id;
          const matchingInput = originatingInputId
            ? newFacetDetailsElement.querySelector(`#${CSS.escape(originatingInputId)}`)
            : null;

          if (matchingInput) {
            matchingInput.focus();
          } else {
            // Fallback to summary/close button if the checkbox can't be found
            const fallbackSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
              ? `.mobile-facets__close-button`
              : `.facets__summary`;
            const fallbackElement = newFacetDetailsElement.querySelector(fallbackSelector);
            if (fallbackElement) fallbackElement.focus();
          }
        }
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);







/* Custom dual price slider for Shopify Dawn 16.x
 *
 * Why the original "max snaps to min":
 *  - MIN_GAP = 100 was clamped against a tiny window (~1 unit), which
 *    forced `min` up and pinned `max` at `min + 100`.
 *  - Native <input type=range> stacking meant only the top thumb was
 *    ever grabbable when both thumbs visually overlapped.
 *
 * This version:
 *  - No gap. Handles may meet; pushing one past the other carries it.
 *  - Custom pointer drag on the wrapper, so the NEAREST thumb is always
 *    the one you grab (no z-index lottery).
 *  - Stops range events before they reach Dawn's facet <form>, so we don't
 *    re-fetch on every drag tick. Commits a single `input` on release.
 *  - Keeps keyboard support (arrow keys on the focused native input).
 */
(() => {
  'use strict';

  const CONTAINER = '[data-price-facet]';
  const SLIDER = '[data-custom-price-slider]';
  const MIN_SLIDER = '[data-price-min-slider]';
  const MAX_SLIDER = '[data-price-max-slider]';
  const MIN_LABEL = '[data-price-min-label]';
  const MAX_LABEL = '[data-price-max-label]';

  const EPSILON = 1e-6;
  const THUMB_PX = 18; // must match the CSS thumb width (1.8rem @ 10px root)

  const initialized = new WeakSet();

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
  const roundMoney = (value) => Math.round(value * 100) / 100;

  function parseAmount(raw) {
    let text = String(raw ?? '').trim();
    if (!text) return NaN;
    text = text.replace(/[^\d.,-]/g, '');
    if (!text) return NaN;

    const lastComma = text.lastIndexOf(',');
    const lastDot = text.lastIndexOf('.');

    if (lastComma !== -1 && lastDot !== -1) {
      text =
        lastComma > lastDot
          ? text.replace(/\./g, '').replace(',', '.')
          : text.replace(/,/g, '');
    } else if (lastComma !== -1) {
      const decimals = text.length - lastComma - 1;
      text = decimals === 3 ? text.replace(/,/g, '') : text.replace(',', '.');
    }

    const number = Number(text);
    return Number.isFinite(number) ? number : NaN;
  }

  function readNumber(value, fallback) {
    const number = parseAmount(value);
    return Number.isFinite(number) ? number : fallback;
  }

  function getParts(container) {
    const slider = container.querySelector(SLIDER);
    if (!slider) return null;

    const minSlider = slider.querySelector(MIN_SLIDER);
    const maxSlider = slider.querySelector(MAX_SLIDER);
    if (!minSlider || !maxSlider) return null;

    const inputs = Array.from(container.querySelectorAll('input'));
    const minField = inputs.find((i) => i.name === slider.dataset.minParam);
    const maxField = inputs.find((i) => i.name === slider.dataset.maxParam);
    if (!minField || !maxField) return null;

    return { container, slider, minSlider, maxSlider, minField, maxField };
  }

  function getBounds(parts) {
    const low = Number(parts.minSlider.min) || 0;
    const high = Number(parts.maxSlider.max);
    if (!Number.isFinite(high) || high <= low) return null;
    return { low, high };
  }

  function formatPrice(slider, amount) {
    const currency = slider.dataset.currency || 'INR';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(amount);
    } catch {
      return `${currency} ${amount}`;
    }
  }

  function updateLabels(parts, min, max) {
    const minLabel = parts.slider.querySelector(MIN_LABEL);
    const maxLabel = parts.slider.querySelector(MAX_LABEL);
    if (minLabel) minLabel.textContent = formatPrice(parts.slider, min);
    if (maxLabel) maxLabel.textContent = formatPrice(parts.slider, max);
  }

  function updateStacking(parts, activeHandle) {
    const min = Number(parts.minSlider.value);
    const max = Number(parts.maxSlider.value);
    const low = Number(parts.minSlider.min) || 0;

    let minZ = '2';
    let maxZ = '3';

    if (Math.abs(min - max) < EPSILON) {
      // Coincident: paint whichever thumb can widen the window on top.
      if (min <= low + EPSILON) {
        minZ = '2';
        maxZ = '3';
      } else {
        minZ = '3';
        maxZ = '2';
      }
    }

    if (activeHandle === 'min') minZ = '4';
    if (activeHandle === 'max') maxZ = '4';

    parts.minSlider.style.zIndex = minZ;
    parts.maxSlider.style.zIndex = maxZ;
  }

  function setFieldValue(field, value, isEmpty) {
    const current = field.value.trim();

    if (isEmpty) {
      if (current === '') return false;
      field.value = '';
      return true;
    }

    const desired = String(value);
    if (current === desired) return false;

    const currentNumber = parseAmount(current);
    if (Number.isFinite(currentNumber) && Math.abs(currentNumber - value) <= EPSILON) {
      return false;
    }

    field.value = desired;
    return true;
  }

  function syncFromFields(container) {
    const parts = getParts(container);
    if (!parts) return;

    const bounds = getBounds(parts);
    if (!bounds) {
      parts.minSlider.disabled = true;
      parts.maxSlider.disabled = true;
      return;
    }

    const { low, high } = bounds;

    let min = clamp(readNumber(parts.minField.value, low), low, high);
    let max = clamp(readNumber(parts.maxField.value, high), low, high);
    if (min > max) min = max;

    parts.minSlider.disabled = false;
    parts.maxSlider.disabled = false;
    parts.minSlider.value = String(min);
    parts.maxSlider.value = String(max);

    updateLabels(
      parts,
      Number(parts.minSlider.value),
      Number(parts.maxSlider.value)
    );
    updateStacking(parts, null);
  }

  /* ---------------------------- dragging ---------------------------- */

  function valueFromClientX(parts, clientX) {
    const bounds = getBounds(parts);
    if (!bounds) return 0;

    const { low, high } = bounds;
    const rect = parts.minSlider.getBoundingClientRect();
    if (!rect.width) return low;

    const usable = Math.max(rect.width - THUMB_PX, 1);
    const offset = clientX - rect.left - THUMB_PX / 2;
    const ratio = clamp(offset / usable, 0, 1);

    return roundMoney(low + ratio * (high - low));
  }

  function applyHandle(parts, handle, value) {
    const bounds = getBounds(parts);
    if (!bounds) return;

    const { low, high } = bounds;

    let min = Number(parts.minSlider.value);
    let max = Number(parts.maxSlider.value);
    if (!Number.isFinite(min)) min = low;
    if (!Number.isFinite(max)) max = high;

    const next = clamp(value, low, high);

    if (handle === 'min') {
      min = next;
      if (min > max) max = min; // carry the max thumb along
    } else {
      max = next;
      if (max < min) min = max; // carry the min thumb along
    }

    parts.minSlider.value = String(min);
    parts.maxSlider.value = String(max);

    updateLabels(parts, min, max);
    updateStacking(parts, handle);
  }

  function commit(container, parts) {
    const bounds = getBounds(parts);
    if (!bounds) return;

    const { low, high } = bounds;
    const min = Number(parts.minSlider.value);
    const max = Number(parts.maxSlider.value);

    const changedMin = setFieldValue(parts.minField, min, min <= low + EPSILON);
    const changedMax = setFieldValue(parts.maxField, max, max >= high - EPSILON);

    if (!changedMin && !changedMax) return;

    // Dawn's FacetFiltersForm listens for bubbled `input` events.
    const field = changedMin ? parts.minField : parts.maxField;
    field.dispatchEvent(new Event('input', { bubbles: true }));
  }

  function onPointerDown(event) {
    if (event.button !== undefined && event.button !== 0) return;
    if (!event.target.closest?.(SLIDER)) return;

    const container = event.currentTarget;
    const parts = getParts(container);
    if (!parts) return;

    const bounds = getBounds(parts);
    if (!bounds) return;

    const { low, high } = bounds;
    let min = Number(parts.minSlider.value);
    let max = Number(parts.maxSlider.value);
    if (!Number.isFinite(min)) min = low;
    if (!Number.isFinite(max)) max = high;

    const value = valueFromClientX(parts, event.clientX);
    const dMin = Math.abs(value - min);
    const dMax = Math.abs(value - max);

    let handle;
    if (dMin < EPSILON && dMax < EPSILON) {
      handle = min <= low + EPSILON ? 'max' : 'min';
    } else if (Math.abs(min - max) < EPSILON) {
      handle = value >= min ? 'max' : 'min';
    } else {
      handle = dMin <= dMax ? 'min' : 'max';
    }

    container._drag = { handle, parts, pointerId: event.pointerId };
    applyHandle(parts, handle, value);

    try {
      container.setPointerCapture(event.pointerId);
    } catch {}

    event.preventDefault();
  }

  function onPointerMove(event) {
    const container = event.currentTarget;
    const drag = container._drag;
    if (!drag || drag.pointerId !== event.pointerId) return;

    const value = valueFromClientX(drag.parts, event.clientX);
    applyHandle(drag.parts, drag.handle, value);
    event.preventDefault();
  }

  function onPointerUp(event) {
    const container = event.currentTarget;
    const drag = container._drag;
    if (!drag || drag.pointerId !== event.pointerId) return;

    container._drag = null;
    try {
      container.releasePointerCapture(event.pointerId);
    } catch {}

    commit(container, drag.parts);
    updateStacking(drag.parts, null);
  }

  /* -------------------- keyboard / text-field sync ------------------ */

  function onContainerInput(event) {
    const container = event.currentTarget;
    const target = event.target;

    if (target.matches?.(MIN_SLIDER) || target.matches?.(MAX_SLIDER)) {
      // Keyboard interaction with the native range input.
      event.stopPropagation();

      const parts = getParts(container);
      if (!parts) return;

      const bounds = getBounds(parts);
      if (!bounds) return;

      const { low, high } = bounds;
      let min = Number(parts.minSlider.value);
      let max = Number(parts.maxSlider.value);
      if (!Number.isFinite(min)) min = low;
      if (!Number.isFinite(max)) max = high;

      if (target === parts.minSlider) {
        min = clamp(min, low, high);
        if (min > max) max = min;
      } else {
        max = clamp(max, low, high);
        if (max < min) min = max;
      }

      parts.minSlider.value = String(min);
      parts.maxSlider.value = String(max);

      updateLabels(parts, min, max);
      updateStacking(parts, target === parts.minSlider ? 'min' : 'max');
      return;
    }

    // Typing in a price text field: sync the thumbs, then let the event
    // bubble so Dawn's FacetFiltersForm can debounce-submit.
    if (target.name) syncFromFields(container);
  }

  function onContainerChange(event) {
    const container = event.currentTarget;
    const target = event.target;

    if (target.matches?.(MIN_SLIDER) || target.matches?.(MAX_SLIDER)) {
      event.stopPropagation();
      const parts = getParts(container);
      if (parts) {
        commit(container, parts);
        updateStacking(parts, null);
      }
      return;
    }

    if (!target.name) return;

    const parts = getParts(container);
    if (!parts) return;
    if (target !== parts.minField && target !== parts.maxField) return;

    const bounds = getBounds(parts);
    if (!bounds) return;

    const { low, high } = bounds;
    const isMin = target === parts.minField;

    let min = clamp(readNumber(parts.minField.value, low), low, high);
    let max = clamp(readNumber(parts.maxField.value, high), low, high);
    if (min > max) {
      if (isMin) min = max;
      else max = min;
    }

    parts.minSlider.value = String(min);
    parts.maxSlider.value = String(max);

    const changedMin = setFieldValue(parts.minField, min, min <= low + EPSILON);
    const changedMax = setFieldValue(parts.maxField, max, max >= high - EPSILON);

    updateLabels(parts, min, max);
    updateStacking(parts, null);

    if (changedMin || changedMax) {
      target.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }

  /* ----------------------------- init ------------------------------ */

  function initialize(root = document) {
    const containers = [];
    if (root.matches?.(CONTAINER)) containers.push(root);
    root.querySelectorAll?.(CONTAINER).forEach((node) => containers.push(node));

    containers.forEach((container) => {
      if (initialized.has(container)) {
        syncFromFields(container);
        return;
      }

      if (!getParts(container)) return;

      initialized.add(container);

      container.addEventListener('pointerdown', onPointerDown);
      container.addEventListener('pointermove', onPointerMove);
      container.addEventListener('pointerup', onPointerUp);
      container.addEventListener('pointercancel', onPointerUp);
      container.addEventListener('input', onContainerInput);
      container.addEventListener('change', onContainerChange);

      syncFromFields(container);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  // Dawn replaces facet markup after filtering.
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        if (node.matches?.(CONTAINER) || node.querySelector?.(CONTAINER)) {
          initialize(node);
        }
      });
    });
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });
})();